import type { PlacedCalendarPiece } from "@edu/shared";
import { OverlapIcon } from "../shell/icons.tsx";
import { dayShort, timeText } from "./common.ts";
import type { GridOpening } from "./openings.ts";

/**
 * Окно записи в колонке календаря (`docs/booking-design.md`, 3.2): пунктирная
 * коробка на своём времени.
 *
 * Три отличия от коробки занятия, и все три нарочно:
 *
 * 1. **Пунктир и прозрачный фон.** Окно это не занятие, а обещание времени, и
 *    сплошная коробка того же вида читалась бы как занятие без ученика
 * 2. **Не перетаскивается** (раздел 7 проекта): свойств жеста у коробки нет, а
 *    во время переноса занятия она не ловит указатель (`.cal[data-dragging]` в
 *    стилях), иначе занятие нельзя было бы положить на открытое время
 * 3. **Своё окно без имени**: оно ничьё, подпись «Для записи», иначе одно
 *    время в пунктире преподаватель не узнаёт (проверено глазами 27.09.2026).
 *    У ученика «Свободно» и имя
 *    преподавателя, если преподавателей в окнах недели больше одного.
 *    Перекрытое занятием своё окно помечено значком: подпись в узкую колонку
 *    телефона не влезает, а слова стоят в раскрытой карточке
 */
export function OpeningBox({
  opening,
  piece,
  zone,
  top,
  height,
  selected,
  onSelect,
}: {
  opening: GridOpening;
  piece: PlacedCalendarPiece;
  zone: string;
  top: string;
  height: string;
  selected: boolean;
  onSelect: (opening: GridOpening, anchor: HTMLElement) => void;
}) {
  const width = 1 / Math.max(piece.lanes, 1);
  const left = piece.lanes === 0 ? 0 : piece.lane / piece.lanes;
  const time = timeText(opening.at, zone);
  const day = dayShort(piece.date);
  return (
    <button
      type="button"
      className="cal__win"
      data-mine={opening.mine}
      data-selected={selected}
      style={{
        top,
        height,
        left: `calc(${String(left * 100)}% + 2px)`,
        width: `calc(${String(width * 100)}% - 4px)`,
      }}
      aria-haspopup="dialog"
      aria-expanded={selected}
      aria-label={
        opening.mine
          ? `Открыто для записи: ${day}, ${time}${opening.covered ? ", в это время у вас занятие" : ""}`
          : `Свободное время: ${day}, ${time}${opening.named ? `, ${opening.teacherName}` : ""}`
      }
      onClick={(e) => onSelect(opening, e.currentTarget)}
    >
      <span className="cal__ev-time">{piece.continues ? "↳" : time}</span>
      {opening.mine ? (
        <span className="cal__ev-who">
          Для записи
          {opening.covered && (
            <span className="cal__win-mark">
              <OverlapIcon className="kind__ico" label="В это время у вас занятие" />
            </span>
          )}
        </span>
      ) : (
        <span className="cal__ev-who">
          Свободно{opening.named && `, ${opening.teacherName}`}
        </span>
      )}
    </button>
  );
}
